import type { MaskItem, Orientation, RegionItem, Section, Skeleton } from 'src/app/controls/anatomy/anatomy.component';

const SKELETON_ORIENTATIONS: Array<keyof Pick<Skeleton, 'back' | 'front' | 'left' | 'right'>> = [
  'back',
  'front',
  'left',
  'right',
];

function regionSections(region: RegionItem): Section[] {
  return [region.mainSection, region.upperSection, region.middleSection, region.lowerSection].filter(
    (section): section is Section => !!section,
  );
}

function sectionMasks(section: Section): MaskItem[] {
  return [
    ...(section.boneParts ?? []),
    ...(section.boneNonInteractiveParts ?? []),
    ...(section.muscleParts ?? []),
    ...(section.muscleNonInteractiveParts ?? []),
  ];
}

/** Interactive + non-interactive bone masks for lists, overlays, and lookups. */
export function sectionBoneMaskItems(section: Section | null | undefined): MaskItem[] {
  if (!section) {
    return [];
  }

  return [...(section.boneParts ?? []), ...(section.boneNonInteractiveParts ?? [])];
}

export function sectionHasBoneView(section: Section | null | undefined): boolean {
  if (!section) {
    return false;
  }

  return sectionBoneMaskItems(section).length > 0;
}

export function findBoneMaskByPartId(section: Section, partId: number): MaskItem | undefined {
  return sectionBoneMaskItems(section).find((item) => item.partId === partId);
}

/** Interactive + non-interactive muscle masks for lists, overlays, and lookups. */
export function sectionMuscleMaskItems(section: Section | null | undefined): MaskItem[] {
  if (!section) {
    return [];
  }

  return [...(section.muscleParts ?? []), ...(section.muscleNonInteractiveParts ?? [])];
}

export function sectionHasMuscleView(section: Section | null | undefined): boolean {
  if (!section) {
    return false;
  }

  return sectionMuscleMaskItems(section).length > 0;
}

export function findMuscleMaskByPartId(section: Section, partId: number): MaskItem | undefined {
  return sectionMuscleMaskItems(section).find((item) => item.partId === partId);
}

export interface SkeletonPartLookup {
  regionId: number;
  regionName: string;
  partIndex: number;
  partName: string;
  maskFileName?: string | null;
  noteRegion?: string | null;
}

/** Treat 0 / missing mask ids as unspecified so we do not match the wrong mask. */
export function normalizeSkeletonMaskId(maskId?: number | null): number | null {
  return maskId != null && maskId > 0 ? maskId : null;
}

/** Locate a mask (and its region) in the skeleton model by partId, optionally matching maskId. */
export function findPartInSkeleton(
  skeleton: Skeleton,
  partId: number,
  maskId?: number | null,
): SkeletonPartLookup | null {
  if (!partId) {
    return null;
  }

  const effectiveMaskId = normalizeSkeletonMaskId(maskId);
  let fallback: SkeletonPartLookup | null = null;

  for (const orientationKey of SKELETON_ORIENTATIONS) {
    const orientation: Orientation | null = skeleton[orientationKey];
    if (!orientation?.regions?.length) {
      continue;
    }

    for (const region of orientation.regions) {
      for (const section of regionSections(region)) {
        for (const mask of sectionMasks(section)) {
          if (mask.partId !== partId) {
            continue;
          }

          const lookup: SkeletonPartLookup = {
            regionId: region.regionId,
            regionName: region.regionName,
            partIndex: mask.index ?? 0,
            partName: mask.partName,
            maskFileName: mask.maskFileName ?? null,
            noteRegion: mask.noteRegion ?? mask.bodyPart?.noteRegion ?? null,
          };

          if (effectiveMaskId != null && mask.maskId === effectiveMaskId) {
            return lookup;
          }

          if (!fallback) {
            fallback = lookup;
          }
        }
      }
    }
  }

  return fallback;
}

export function resolveRegionNameFromSkeleton(
  skeleton: Skeleton | null | undefined,
  partId: number | null | undefined,
  maskId?: number | null,
): string {
  if (!skeleton || !partId) {
    return '';
  }

  return findPartInSkeleton(skeleton, partId, maskId)?.regionName ?? '';
}

/** Sort order aligned with skeleton regionId values from model.json (Head/Spine first, then extremities). */
export function skeletonRegionSortBase(regionId: number | null | undefined): number {
  const regionOrder: Record<number, number> = {
    2: 10_000, // Head/Spine/Pelvis
    18: 60_000, // Upper Left Extremities
    21: 65_000, // Upper Right Extremities
    4: 70_000, // Lower Left Extremities
    7: 75_000, // Lower Right Extremities
  };

  return regionId != null ? (regionOrder[regionId] ?? 90_000) : 90_000;
}

export function skeletonPartSortScore(
  regionId: number | null | undefined,
  partName: string,
  partIndex?: number,
): number {
  const baseScore = skeletonRegionSortBase(regionId);
  if (partIndex != null && partIndex > 0) {
    return baseScore + partIndex;
  }

  const match = partName.match(/\d+/);
  if (match) {
    return baseScore + parseInt(match[0], 10);
  }

  return baseScore + (partName.toUpperCase().charCodeAt(0) || 0);
}
